import { Optional } from '@vimeo/core/types/generic';

import { detectBrowser, ESupportedBrowser, IDeviceInfo } from '../utils/browser/browser_detection';
import { inline } from '../utils/general/lambda';
import { environmentConfig } from './EnvironmentConfig';

/**
 * Current environment browser capabilities and configuration.
 */
export const browserConfig = {
  BROWSER: detectBrowser() as Optional<IDeviceInfo>,
  /**
   * Browser support for agora SDK based on source code of library.
   */
  AGORA_SUPPORT: {
    // Same engine as chrome, but started as ~80 (February 7, 2020).
    [ESupportedBrowser.EDGE_CHROMIUM]: {
      version: '80',
    },
    [ESupportedBrowser.CHROME]: {
      version: '58',
    },
    [ESupportedBrowser.FIREFOX]: {
      version: '56',
    },
    [ESupportedBrowser.SAFARI]: {
      version: '12',
    },
    [ESupportedBrowser.OPERA]: {
      version: '45',
    },
    [ESupportedBrowser.IOS]: {
      version: '15.1', // https://groups.google.com/g/discuss-webrtc/c/WfPSqEJ6gl8?pli=1
    },
  },
  IS_IN_IFRAME: (() => {
    try {
      return window.self !== window.top;
    } catch (error) {
      return true;
    }
  })(),
  /**
   * Feature checks in current browser environment, can be useful to test specific item browser support in 1 place.
   */
  FEATURE: {
    CAN_USE_FULLSCREEN_MODE:
      !environmentConfig.IS_SSR &&
      ('requestFullscreen' in window.document.body ||
        'webkitRequestFullscreen' in window.document.body) &&
      ('exitFullscreen' in window.document || 'webkitExitFullscreen' in window.document),
    CAN_USE_PERFORMANCE_MARKS:
      !environmentConfig.IS_SSR && typeof window.performance?.mark === 'function',
    CAN_USE_AUDIO_CONTEXT: !environmentConfig.IS_SSR && 'AudioContext' in window,
    CAN_USE_IMAGE_CAPTURE: !environmentConfig.IS_SSR && 'ImageCapture' in window,
    CAN_USE_WEB_SOCKETS: !environmentConfig.IS_SSR && 'WebSocket' in window,
    CAN_USE_OUTPUT_DEVICE: !environmentConfig.IS_SSR && 'setSinkId' in HTMLMediaElement.prototype,
    CAN_USE_WEBRTC: inline((): boolean => {
      return Boolean(
        !environmentConfig.IS_SSR &&
          'RTCPeerConnection' in window &&
          'MediaStream' in window &&
          navigator.mediaDevices &&
          navigator.mediaDevices.getUserMedia,
      );
    }),
    /**
     * Try to create webgl context in current environment context to confirm whether it is accessible:
     * https://developer.mozilla.org/en-US/docs/Web/API/WebGL_API/By_example/Detect_WebGL
     *
     * @returns whether can use WebGL API in current browser environment
     */
    CAN_USE_WEBGL: inline(() => {
      if (environmentConfig.IS_TEST) {
        return false;
      }

      try {
        const canvas: HTMLCanvasElement = document.createElement('canvas');

        return Boolean(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
      } catch (error) {
        return false;
      }
    }),
  },
  /**
   * Keep checking availability in a moment with getter, try to fully rely on checks before direct init/creation.
   *
   * @returns whether broadcast channel API can be used
   */
  CAN_USE_BROADCAST_CHANNEL(): boolean {
    if (!window.BroadcastChannel) {
      return false;
    }

    // Try to create actual channel to verify if partitions API is not blocking any operations.
    // Without runtime check we cannot be sure.
    //
    // @see https://developer.mozilla.org/en-US/docs/Web/Privacy/State_Partitioning
    try {
      new window.BroadcastChannel('test_channel').close();

      return true;
    } catch (error) {
      return false;
    }
  },
};
